/**
 * @file seo.js
 * @description Centralized per-page <head> management for the SPA. CRA is
 *   client-rendered, so each public/SEO route sets its own title, meta
 *   description, canonical, Open Graph, Twitter card, <html lang>, and (when
 *   needed) a robots noindex tag imperatively. This consolidates the scattered
 *   useEffect/document.querySelector logic that previously lived inside each
 *   landing component (HomePage, PayTrackerLanding, Therapist/AgencyLanding) so
 *   that:
 *     1. every prerendered route (react-snap) captures a COMPLETE head — OG and
 *        Twitter tags were never updated per-route before, only the static ones
 *        in index.html;
 *     2. preview/backup routes can emit <meta name="robots" content=
 *        "noindex,nofollow"> with a single flag.
 *
 *   Mirrors the existing imperative approach (see lib/seoSchema.js for JSON-LD).
 *   On unmount it restores whatever was there before (or removes tags it created)
 *   so SPA navigation between pages never leaks one page's head into the next.
 * @module lib/seo
 */
import { useEffect } from "react";
import seoRoutesData from "../data/seoRoutes.json";
import { currentHtmlLang } from "../i18n";

const SITE = "https://abacases.com";

// Slugs served as prerendered /route/index.html — their live 200 URL carries a
// trailing slash (the bare form 308-redirects). Excludes "/" and /es
// (prerender:false), which serve un-slashed via the SPA fallback.
const PRERENDERED_SLUGS = new Set(
  (seoRoutesData.routes || [])
    .filter((r) => r.prerender !== false && r.path !== "/")
    .map((r) => r.path.replace(/^\/+/, ""))
);

/**
 * Canonical in-app href for a view/slug. Prerendered SEO routes get the trailing
 * slash they actually serve at, so internal links point straight at the 200 URL
 * instead of the bare form that 308-redirects (cleaner crawl paths, and no
 * internal links feeding the "Page with redirect" report). App views (cases-v2,
 * dashboard, home…) and /es stay un-slashed.
 * @param {string} to view id / slug, with or without a leading slash
 * @returns {string} the href to use
 */
export function seoHref(to) {
  const slug = String(to || "").replace(/^\/+/, "");
  return PRERENDERED_SLUGS.has(slug) ? `/${slug}/` : `/${slug}`;
}

/**
 * Find a <meta>/<link> by selector, or create it (appended to <head>) if absent.
 * Returns { el, created } so cleanup can remove only the tags we introduced.
 */
function ensureTag(selector, make) {
  let el = document.head.querySelector(selector);
  if (el) return { el, created: false };
  el = make();
  document.head.appendChild(el);
  return { el, created: true };
}

function metaByName(name) {
  return ensureTag(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  });
}

function metaByProperty(property) {
  return ensureTag(`meta[property="${property}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", property);
    return m;
  });
}

function linkByRel(rel) {
  return ensureTag(`link[rel="${rel}"]`, () => {
    const l = document.createElement("link");
    l.setAttribute("rel", rel);
    return l;
  });
}

/**
 * Set an attribute on a managed tag and return a restore() thunk that puts the
 * previous value back — or removes the tag entirely if we created it.
 */
function setManaged({ el, created }, attr, value) {
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => {
    if (created) {
      el.remove();
    } else if (prev != null) {
      el.setAttribute(attr, prev);
    }
  };
}

/**
 * usePageSeo — declaratively own the document head for a public/SEO route.
 *
 * @param {Object}  opts
 * @param {string}  opts.title          document <title>
 * @param {string}  opts.description    meta description (also OG/Twitter fallback)
 * @param {string}  [opts.canonical]    absolute or path; path is resolved against the site origin
 * @param {string}  [opts.ogTitle]      defaults to title
 * @param {string}  [opts.ogDescription] defaults to description
 * @param {string}  [opts.ogUrl]        defaults to canonical
 * @param {string}  [opts.ogImage]      absolute OG image URL (optional; index.html has a default)
 * @param {boolean} [opts.noindex]      when true emit <meta name="robots" content="noindex,nofollow">
 * @param {string}  [opts.lang]         <html lang>; defaults to inferring from canonical/path ("/es" → es)
 */
export function usePageSeo({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  noindex = false,
  lang,
} = {}) {
  // Stable dependency so the effect re-runs when any field changes (e.g. language).
  const key = JSON.stringify([title, description, canonical, ogTitle, ogDescription, ogUrl, ogImage, noindex, lang]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const abs = (u) => {
      if (!u) return undefined;
      return /^https?:\/\//.test(u) ? u : `${SITE}${u.startsWith("/") ? "" : "/"}${u}`;
    };
    // Prerendered SEO routes are emitted as build/<route>/index.html, so their
    // live 200 URL carries a trailing slash (Cloudflare 308-redirects the
    // un-slashed form). The self-referential canonical + og:url must include it,
    // or the page declares its own canonical as a URL that redirects — which
    // Google reports as "Page with redirect" and won't index. Exceptions: "/"
    // and "/es" (the home twins) render via the SPA fallback and serve 200
    // un-slashed. Never touch query/hash or asset (og:image) URLs.
    const withSlash = (u) => {
      if (!u) return u;
      const s = String(u);
      if (/[?#]/.test(s) || s.endsWith("/") || s.endsWith("/es")) return s;
      if (/\/[^/]*\.[^/]+$/.test(s)) return s; // last path segment is a file
      return s + "/";
    };
    const canonUrl = withSlash(abs(canonical));
    const resolvedOgUrl = withSlash(abs(ogUrl)) || canonUrl;
    const resolvedOgTitle = ogTitle || title;
    const resolvedOgDesc = ogDescription || description;

    const restores = [];

    // <title>
    const prevTitle = document.title;
    if (title) document.title = title;
    restores.push(() => { document.title = prevTitle; });

    // meta description
    if (description) restores.push(setManaged(metaByName("description"), "content", description));

    // canonical
    if (canonUrl) restores.push(setManaged(linkByRel("canonical"), "href", canonUrl));

    // robots (noindex for preview/backup routes)
    if (noindex) restores.push(setManaged(metaByName("robots"), "content", "noindex,nofollow"));

    // Open Graph
    if (resolvedOgTitle) restores.push(setManaged(metaByProperty("og:title"), "content", resolvedOgTitle));
    if (resolvedOgDesc)  restores.push(setManaged(metaByProperty("og:description"), "content", resolvedOgDesc));
    if (resolvedOgUrl)   restores.push(setManaged(metaByProperty("og:url"), "content", resolvedOgUrl));
    if (ogImage)         restores.push(setManaged(metaByProperty("og:image"), "content", abs(ogImage)));

    // Twitter card
    if (resolvedOgTitle) restores.push(setManaged(metaByName("twitter:title"), "content", resolvedOgTitle));
    if (resolvedOgDesc)  restores.push(setManaged(metaByName("twitter:description"), "content", resolvedOgDesc));
    if (ogImage)         restores.push(setManaged(metaByName("twitter:image"), "content", abs(ogImage)));

    // <html lang>
    // Default is the UI language, which src/i18n.js keeps on <html> for every page;
    // restore to that (not to a value captured at mount, which goes stale when the
    // language is switched while this page is open).
    const inferEs = (canonical || "").replace(/\/+$/, "").endsWith("/es");
    const nextLang = lang || (inferEs ? "es" : currentHtmlLang());
    document.documentElement.lang = nextLang;
    restores.push(() => { document.documentElement.lang = currentHtmlLang(); });

    return () => { restores.forEach((fn) => fn()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export { SITE };
